import { getRangeMs, useFeatureFlags } from '@chronosphereio/core';
import { useEffect, useMemo, useRef } from 'react';
import { useDataQueryResult } from '@/plugin-system';
import { useTimeRange } from '@/plugin-system/runtime/TimeRangeProviderV2';
import { XAxisTimeRange } from '../time-series-chart-model';
import { TimeSeriesDatasetResponse } from './dataset';

export interface XAxisBounds {
  xAxisMinMs: number;
  xAxisMaxMs: number;
  xAxisRangeMs: number;
}

export interface StablePanelSnapshot {
  datasetResponse: TimeSeriesDatasetResponse;
  xAxisBounds: XAxisBounds;
}

/**
 * Produces a stable render snapshot of the dataset and x-axis bounds during refetch.
 * While data is being fetched, holds the previous snapshot so the chart does not
 * render a mixed state (e.g. new time range with old data, causing axis flicker).
 * When fetching settles with data, atomically updates both values together.
 * Handles `xAxisTimeRange`:
 *   - `'time-range-context'` (default): bounds come from `useTimeRange().absoluteTimeRange`
 *   - `'data'`: bounds come from the dataset `timeScale`
 */
export function useStablePanelSnapshot({
  datasetResponse,
  xAxisTimeRange = 'time-range-context',
}: {
  datasetResponse: TimeSeriesDatasetResponse;
  xAxisTimeRange?: XAxisTimeRange;
}): StablePanelSnapshot {
  const { enableTimeSeriesChartStableFetch } = useFeatureFlags();
  const { isFetching } = useDataQueryResult({ dataTypeFilter: 'TimeSeries' });
  const { absoluteTimeRange } = useTimeRange();
  const rangeMs = getRangeMs(absoluteTimeRange);

  const startMs = absoluteTimeRange.start.valueOf();
  const endMs = absoluteTimeRange.end.valueOf();
  const timeScale = datasetResponse.timeScale;

  const liveXAxisBounds: XAxisBounds = useMemo(() => {
    if (xAxisTimeRange === 'data' && timeScale) {
      return {
        xAxisMinMs: timeScale.startMs,
        xAxisMaxMs: timeScale.endMs,
        xAxisRangeMs: timeScale.rangeMs,
      };
    }
    return {
      xAxisMinMs: startMs,
      xAxisMaxMs: endMs,
      xAxisRangeMs: rangeMs,
    };
  }, [xAxisTimeRange, timeScale, startMs, endMs, rangeMs]);

  const snapshotRef = useRef<StablePanelSnapshot>({
    datasetResponse,
    xAxisBounds: liveXAxisBounds,
  });

  // The effect keeps the ref in sync with the latest settled state so the
  // *next* fetch cycle has a correct snapshot to hold. Because effects run
  // after render, the non-fetching return path below already provides fresh
  // values for the current render; the effect merely "saves" them for later.
  useEffect(() => {
    if (enableTimeSeriesChartStableFetch && !isFetching) {
      snapshotRef.current = {
        datasetResponse,
        xAxisBounds: liveXAxisBounds,
      };
    }
  }, [enableTimeSeriesChartStableFetch, isFetching, datasetResponse, liveXAxisBounds]);

  if (enableTimeSeriesChartStableFetch && isFetching) {
    return snapshotRef.current;
  }

  return { datasetResponse, xAxisBounds: liveXAxisBounds };
}
